import { useCallback, useState, useEffect } from 'react';
import type { SiteManagedPage } from '../data/siteManagedPages';
import { siteStaffBannerService, type SiteStaffBannerRow } from '../services/supabaseService';
import {
  defaultSitePageConfig,
  mergePageConfig,
  serializePageConfig,
  type SitePageConfig,
} from '../lib/sitePageConfig';

const STORAGE_KEY = 'd88_site_staff_updates_v2';
const LEGACY_STORAGE_KEY = 'd88_site_staff_updates_v1';

export interface StaffPageUpdate {
  path: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  active: boolean;
  updatedAt: string;
  updatedBy?: string;
  pageConfig: SitePageConfig;
}

function normalizeEntry(pathKey: string, v: unknown): StaffPageUpdate | null {
  if (!v || typeof v !== 'object') return null;
  const e = v as Partial<StaffPageUpdate>;
  return {
    path: e.path ?? pathKey,
    headline: e.headline ?? '',
    body: e.body ?? '',
    ctaLabel: e.ctaLabel ?? '',
    ctaHref: e.ctaHref ?? '',
    active: !!e.active,
    updatedAt: e.updatedAt ?? '',
    updatedBy: e.updatedBy,
    pageConfig: mergePageConfig(e.pageConfig),
  };
}

function rowToUpdate(r: SiteStaffBannerRow): StaffPageUpdate {
  return {
    path: r.path_prefix,
    headline: r.headline || '',
    body: r.body || '',
    ctaLabel: r.cta_label || '',
    ctaHref: r.cta_href || '',
    active: !!r.active,
    updatedAt: r.updated_at,
    updatedBy: r.updated_by ?? undefined,
    pageConfig: mergePageConfig(r.page_config ?? {}),
  };
}

function writeLocal(map: Record<string, StaffPageUpdate>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function readLocal(): Record<string, StaffPageUpdate> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, StaffPageUpdate> = {};
      for (const [k, v] of Object.entries(parsed)) {
        const n = normalizeEntry(k, v);
        if (n) out[k] = n;
      }
      return out;
    }
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy) return {};
    const parsedLegacy = JSON.parse(legacy) as Record<string, unknown>;
    const upgraded: Record<string, StaffPageUpdate> = {};
    for (const [k, v] of Object.entries(parsedLegacy)) {
      const n = normalizeEntry(k, v);
      if (n) upgraded[k] = n;
    }
    writeLocal(upgraded);
    return upgraded;
  } catch {
    return {};
  }
}

function broadcast() {
  try {
    window.dispatchEvent(new Event('d88-site-updates'));
  } catch {
    /* ignore */
  }
}

/** Pick banner for current route from a path → update map. */
export function pickStaffBanner(pathname: string, byPath: Record<string, StaffPageUpdate>): StaffPageUpdate | null {
  const active = (u: StaffPageUpdate | undefined) =>
    !!u?.active && !!(u.headline?.trim() || u.body?.trim());

  if (pathname === '/district' || pathname === '/') {
    const g = byPath['__global__'];
    return active(g) ? g! : null;
  }

  const keys = Object.keys(byPath)
    .filter((k) => k !== '__global__')
    .sort((a, b) => b.length - a.length);

  for (const k of keys) {
    const u = byPath[k];
    if (!active(u)) continue;
    if (pathname === k || pathname.startsWith(`${k}/`)) return u!;
  }
  return null;
}

/**
 * Longest-prefix match for the current path (including inactive banners).
 * Used for editorial / feature strip below the nav.
 */
export function pickStaffPageEditorial(pathname: string, byPath: Record<string, StaffPageUpdate>): StaffPageUpdate | null {
  if (pathname === '/district' || pathname === '/') {
    return byPath['__global__'] ?? null;
  }
  const keys = Object.keys(byPath)
    .filter((k) => k !== '__global__')
    .sort((a, b) => b.length - a.length);
  for (const k of keys) {
    const u = byPath[k];
    if (pathname === k || pathname.startsWith(`${k}/`)) return u ?? null;
  }
  return null;
}

export function useSitePageUpdates() {
  const [map, setMap] = useState<Record<string, StaffPageUpdate>>({});
  const [source, setSource] = useState<'remote' | 'local'>('remote');
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await siteStaffBannerService.getAll();
    if (error) {
      const local = readLocal();
      setMap(local);
      setSource('local');
      setLoadError(error.message || 'Could not load banners — using local copy if any.');
      return;
    }
    const next: Record<string, StaffPageUpdate> = {};
    for (const row of data || []) {
      next[row.path_prefix] = rowToUpdate(row);
    }
    setMap(next);
    setSource('remote');
    setLoadError(null);
    writeLocal(next);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    const onCustom = () => refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('d88-site-updates', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('d88-site-updates', onCustom);
    };
  }, [refresh]);

  const getForPathPrefix = useCallback((path: string) => map[path] || null, [map]);

  const saveUpdate = useCallback(
    async (path: string, patch: Partial<StaffPageUpdate> & { updatedBy?: string }, pageMeta?: SiteManagedPage) => {
      const prev =
        map[path] ||
        ({
          path,
          headline: '',
          body: '',
          ctaLabel: '',
          ctaHref: '',
          active: false,
          updatedAt: '',
          pageConfig: defaultSitePageConfig(),
        } satisfies StaffPageUpdate);

      const pageConfig =
        patch.pageConfig !== undefined ? mergePageConfig(patch.pageConfig as unknown) : prev.pageConfig;

      const next: StaffPageUpdate = {
        ...prev,
        path: pageMeta?.path ?? path,
        headline: patch.headline ?? prev.headline,
        body: patch.body ?? prev.body,
        ctaLabel: patch.ctaLabel ?? prev.ctaLabel,
        ctaHref: patch.ctaHref ?? prev.ctaHref,
        active: patch.active ?? prev.active,
        updatedAt: new Date().toISOString(),
        updatedBy: patch.updatedBy ?? prev.updatedBy,
        pageConfig,
      };

      const key = pageMeta?.path ?? path;
      const { error } = await siteStaffBannerService.upsert({
        path_prefix: key,
        headline: next.headline,
        body: next.body,
        cta_label: next.ctaLabel,
        cta_href: next.ctaHref,
        active: next.active,
        updated_by: next.updatedBy,
        page_config: serializePageConfig(next.pageConfig),
      });

      if (error) {
        const merged = { ...map, [key]: next };
        writeLocal(merged);
        setMap(merged);
        setSource('local');
        setLoadError(error.message);
      } else {
        await refresh();
      }
      broadcast();
    },
    [map, refresh]
  );

  const clearUpdate = useCallback(
    async (path: string) => {
      const { error } = await siteStaffBannerService.remove(path);
      const next = { ...map };
      delete next[path];
      if (error) {
        writeLocal(next);
        setMap(next);
        setLoadError(error.message);
      } else {
        await refresh();
      }
      broadcast();
    },
    [map, refresh]
  );

  return { map, getForPathPrefix, saveUpdate, clearUpdate, refresh, source, loadError };
}

/** Public banner map for layout (all visitors). */
export function usePublicStaffBanners() {
  const [byPath, setByPath] = useState<Record<string, StaffPageUpdate>>({});

  const load = useCallback(async () => {
    const { data, error } = await siteStaffBannerService.getAll();
    if (error && (!data || data.length === 0)) {
      setByPath(readLocal());
      return;
    }
    const next: Record<string, StaffPageUpdate> = {};
    for (const row of data || []) {
      next[row.path_prefix] = rowToUpdate(row);
    }
    setByPath(next);
    writeLocal(next);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onCustom = () => load();
    window.addEventListener('d88-site-updates', onCustom);
    return () => window.removeEventListener('d88-site-updates', onCustom);
  }, [load]);

  return byPath;
}
